import type { Summary } from '@memori-web/shared';

export interface AiSummaryCallbacks {
  onGenerate: (date: string) => Promise<Summary>;
}

export function renderAiSummary(
  root: HTMLElement,
  date: string,
  cached: Summary | null,
  cb: AiSummaryCallbacks,
) {
  if (cached) {
    renderResult(root, cached, () => generate());
    return;
  }
  renderIdle(root, () => generate());

  async function generate() {
    renderProgress(root);
    try {
      const result = await cb.onGenerate(date);
      renderResult(root, result, () => generate());
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : String(err);
      renderError(root, detail, () => generate());
    }
  }
}

function renderIdle(root: HTMLElement, onGenerate: () => void) {
  root.innerHTML = `
    <div class="ai-summary">
      <div class="ai-summary__head">✨ AI 要約</div>
      <button type="button" class="ai-summary__btn" id="ai-generate">✨ この日を要約する</button>
    </div>
  `;
  root.querySelector('#ai-generate')!.addEventListener('click', onGenerate);
}

function renderProgress(root: HTMLElement) {
  root.innerHTML = `
    <div class="ai-summary">
      <div class="ai-summary__head">✨ AI 要約</div>
      <div class="ai-summary__progress" aria-live="polite">
        <span class="ai-summary__shimmer"></span>
        <span>要約を生成中...</span>
      </div>
    </div>
  `;
}

function renderResult(root: HTMLElement, r: Summary, onRegenerate: () => void) {
  root.innerHTML = `
    <div class="ai-summary ai-summary--filled">
      <div class="ai-summary__head">✨ AI 要約</div>
      <div class="ai-summary__card">
        <h3 class="ai-summary__headline">${escape(r.headline)}</h3>
        <ul class="ai-summary__bullets">
          ${r.bullets.map(b => `<li>${escape(b)}</li>`).join('')}
        </ul>
        <div class="ai-summary__meta">
          <span>${r.sourceCount} 件のメモから / ${formatTime(r.generatedAt)}</span>
          <button type="button" class="ai-summary__action" id="ai-copy">📋</button>
          <button type="button" class="ai-summary__action" id="ai-regen">🔄</button>
        </div>
      </div>
    </div>
  `;
  root.querySelector('#ai-copy')!.addEventListener('click', () => {
    const text = `${r.headline}\n\n${r.bullets.map(b => `- ${b}`).join('\n')}`;
    navigator.clipboard.writeText(text);
  });
  root.querySelector('#ai-regen')!.addEventListener('click', onRegenerate);
}

function renderError(root: HTMLElement, detail: string, onRetry: () => void) {
  root.innerHTML = `
    <div class="ai-summary ai-summary--error">
      <div class="ai-summary__head">✨ AI 要約</div>
      <p>要約に失敗しました</p>
      <pre class="ai-summary__error-detail">${escape(detail)}</pre>
      <button type="button" class="ai-summary__btn" id="ai-retry">🔄 再試行</button>
    </div>
  `;
  root.querySelector('#ai-retry')!.addEventListener('click', onRetry);
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]!));
}
function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}
